/**
 * Sammelt die Bewegungseingaben des Spielers (Masterprompt Abschnitte 43, 81).
 *
 * Zwei Quellen, ein Ergebnis:
 * - Desktop: WASD oder die Pfeiltasten
 * - Mobil:   virtueller Joystick (wird von der Oberflaeche gespeist)
 *
 * Die Klasse liefert nur eine Richtung - niemals eine Position. Wohin der Hero
 * dadurch tatsaechlich kommt, entscheidet allein der Server
 * (Masterprompt Abschnitt 28).
 */

export interface MoveVector {
  /** Richtung entlang der Lane, -1 bis 1. Positiv = Richtung eigenes Portal. */
  x: number;
  /** Richtung quer zur Lane, -1 bis 1. */
  y: number;
}

import { shouldHandleShortcut } from './keyboard.js';

/** Tasten, die als Bewegung gelten. */
const KEY_BINDINGS: Readonly<Record<string, MoveVector>> = {
  // Nach vorne heisst: Richtung Gegner, also entlang der negativen X-Achse.
  KeyW: { x: -1, y: 0 },
  ArrowUp: { x: -1, y: 0 },
  KeyS: { x: 1, y: 0 },
  ArrowDown: { x: 1, y: 0 },
  KeyA: { x: 0, y: 1 },
  ArrowLeft: { x: 0, y: 1 },
  KeyD: { x: 0, y: -1 },
  ArrowRight: { x: 0, y: -1 },
};

export class InputController {
  private pressedKeys = new Set<string>();
  private joystick: MoveVector = { x: 0, y: 0 };
  /**
   * Zaehlen die Lauftasten mit?
   *
   * In der Maussteuerung nicht (Produktentscheidung vom 25.10.2026): Dort
   * liegt Skill 2 auf dem W. Ein Druck darauf wuerde sonst gleichzeitig
   * zaubern und laufen.
   */
  private tastenErlaubt = true;

  constructor() {
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
    // Wechselt der Spieler das Fenster, duerfen keine Tasten "haengen bleiben".
    window.addEventListener('blur', this.releaseAll);
  }

  destroy(): void {
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    window.removeEventListener('blur', this.releaseAll);
    this.releaseAll();
  }

  /**
   * Schaltet die Lauftasten ein oder aus (25.10.2026).
   *
   * Beim Ausschalten werden gehaltene Tasten losgelassen - sonst laeuft der
   * Hero nach dem Umschalten in eine Richtung weiter, die niemand mehr
   * drueckt.
   */
  setTastenErlaubt(erlaubt: boolean): void {
    this.tastenErlaubt = erlaubt;
    if (!erlaubt) this.pressedKeys.clear();
  }

  /** Wird von der Joystick-Oberflaeche aufgerufen. Werte zwischen -1 und 1. */
  setJoystick(x: number, y: number): void {
    this.joystick = { x, y };
  }

  /**
   * Umrechnung von Bildschirmrichtung in Spielrichtung.
   *
   * Der Joystick kennt nur "nach oben" und "nach rechts" auf dem Bildschirm.
   * Die Kamera blickt aber entlang der negativen X-Achse die Lane hinunter.
   * Daraus ergibt sich:
   *
   *   Bildschirm nach oben  -> Richtung Gegner   -> negative X
   *   Bildschirm nach rechts -> nach rechts quer -> negative Y
   *
   * Diese Umrechnung steht bewusst nur an dieser einen Stelle.
   *
   * @param screenX  -1 (links) bis 1 (rechts)
   * @param screenY  -1 (oben) bis 1 (unten)
   */
  setJoystickFromScreen(screenX: number, screenY: number): void {
    this.setJoystick(screenY, -screenX);
  }

  /**
   * Die aktuelle Bewegungsrichtung.
   *
   * Der Joystick hat Vorrang: Wer ihn benutzt, sitzt am Handy und hat keine
   * Tastatur. Die Laenge wird hier NICHT begrenzt - das macht die gemeinsame
   * Spielregel in @dueline/game-rules, damit Client und Server garantiert
   * dasselbe rechnen.
   */
  getMoveVector(): MoveVector {
    if (this.joystick.x !== 0 || this.joystick.y !== 0) {
      return this.joystick;
    }

    if (!this.tastenErlaubt) return { x: 0, y: 0 };

    let x = 0;
    let y = 0;
    for (const code of this.pressedKeys) {
      const binding = KEY_BINDINGS[code];
      if (!binding) continue;
      x += binding.x;
      y += binding.y;
    }
    return { x, y };
  }

  /** True, solange der Spieler sich bewegen moechte. */
  isMoving(): boolean {
    const { x, y } = this.getMoveVector();
    return x !== 0 || y !== 0;
  }

  // ------------------------------------------------------------------ intern

  private handleKeyDown = (event: KeyboardEvent): void => {
    // Wer in ein Eingabefeld tippt, will nicht laufen (25.09.2026).
    if (!shouldHandleShortcut(event)) return;
    // In der Maussteuerung ist WASD ein Skill, keine Bewegung (25.10.2026).
    if (!this.tastenErlaubt) return;
    if (!(event.code in KEY_BINDINGS)) return;
    // Verhindert, dass die Pfeiltasten die Seite scrollen.
    event.preventDefault();
    this.pressedKeys.add(event.code);
  };

  private handleKeyUp = (event: KeyboardEvent): void => {
    this.pressedKeys.delete(event.code);
  };

  private releaseAll = (): void => {
    this.pressedKeys.clear();
    this.joystick = { x: 0, y: 0 };
  };
}
